"use client";

import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { useUser } from "@/shared/contexts/UserContext";

export default function PrivacyPolicyPage() {
  const { isAuthenticated } = useUser();

  return (
    <div className="min-h-screen bg-cream">
      <header className="flex justify-between items-center p-4">
        <Link
          href="/"
          className="font-patrick text-2xl md:text-3xl text-charcoal font-bold"
        >
          <div
            className="font-winky text-3xl text-charcoal font-black p-0 m-0"
          >
            <img src="/logo.png" alt="Hey Hoku" width={256} height={263} className="h-10 w-auto" />
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <Button
              asChild
              className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
            >
              <Link href="/user/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login?action=signup">Sign Up</Link>
              </Button>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-base px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login">Login</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#ffd372]">
          <h1 className="font-winky text-4xl font-bold text-charcoal mb-8">
            Privacy Policy
          </h1>

          <div className="prose prose-lg max-w-none font-winky text-charcoal space-y-6">
            <p className="text-sm text-gray-600 mb-6">
              Effective Date: August 18, 2025
            </p>

            <p>
              Lilia Fashion House Private Limited (“Company,” “we,” “us”) is
              committed to protecting your privacy when you use Hey Hoku
              (“Services”). This Privacy Policy outlines our practices and your
              rights concerning your personal information.
            </p>

            {/* === SECTION 1 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">1. SCOPE</h2>
            <p>
              This policy applies to all use of the Hey Hoku website, web app,
              progressive web app, and mobile applications.
            </p>

            {/* === SECTION 2 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              2. INFORMATION WE COLLECT
            </h2>
            <h3 className="font-bold text-lg mt-6 mb-3">
              2.1. Personal Information provided by you:
            </h3>
            <ul className="list-disc ml-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>
                Photos (wardrobe items; optionally, selfies for personalized
                suggestions)
              </li>
              <li>Physical attributes and style preferences</li>
              <li>
                Payment information (processed via Razorpay; no card details
                stored by us)
              </li>
            </ul>
            <h3 className="font-bold text-lg mt-6 mb-3">
              2.2. Automatically collected data:
            </h3>
            <ul className="list-disc ml-6 space-y-2">
              <li>Device information (model, operating system, browser)</li>
              <li>App usage data</li>
              <li>IP address</li>
              <li>
                Cookie and analytics data (Google Analytics, Meta, Vercel)
              </li>
            </ul>

            {/* === SECTION 3 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              3. PURPOSES OF PROCESSING
            </h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>To provide, personalize, and improve the Services</li>
              <li>To process payments for paid features</li>
              <li>
                To send service-related communications, notifications, and
                important updates
              </li>
              <li>
                To facilitate account security, fraud detection, and customer
                support
              </li>
              <li>
                To analyze and optimize user experience and app performance
              </li>
              <li>To comply with legal and regulatory obligations</li>
            </ul>

            {/* === SECTION 4 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              4. LEGAL BASIS FOR PROCESSING
            </h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                Contractual necessity: Providing the Services, processing
                payments, fulfilling requests
              </li>
              <li>
                Consent: Marketing communications, personalized style
                suggestions, cookies
              </li>
              <li>
                Legal compliance: Regulatory, security, or law enforcement
                obligations
              </li>
              <li>
                Legitimate interests: Improvement and optimization of services,
                business analytics
              </li>
            </ul>

            {/* === SECTION 5 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              5. DATA MINIMIZATION & RETENTION
            </h2>
            <p>Personal data is processed only for legitimate purposes.</p>
            <p>
              Data is retained as long as your account remains active; we may
              retain for up to 1 year subsequent to inactivity for account
              recovery.
            </p>
            <p>
              Photos processed for AI styling may be stored for up to 24 months
              from your last activity or until you delete your account, after
              which photos and personal data are erased.
            </p>

            {/* === SECTION 6 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              6. DATA SUBJECT RIGHTS
            </h2>
            <p>You have the right to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccuracies</li>
              <li>Request data deletion (account deletion)</li>
              <li>Object to certain processing activities</li>
            </ul>
            <p>
              To exercise your rights, email swethaa@heyhoku.ai or
              lilia@liliafashionhouse.com. Requests are processed within 30
              days; backup deletion may require up to 90 days.
            </p>

            {/* === SECTION 7 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              7. SHARING AND DISCLOSURE
            </h2>
            <p>We do not sell or rent your personal information.</p>
            <p>Data is processed by trusted service providers:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                Google Cloud Platform: for secure hosting and storage of user
                data
              </li>
              <li>Vercel: for web hosting and analytics</li>
              <li>Razorpay: for payment processing</li>
              <li>Meta and Google: for analytics (when ads run)</li>
            </ul>
            <p>
              We may share anonymized or aggregated data for business analytics.
            </p>
            <p>
              We may disclose data to legal authorities pursuant to applicable
              law.
            </p>
            <p>
              External sharing of user content (photos, styling) is done only
              with your agreement via share features.
            </p>

            <h3 className="font-bold text-lg mt-6 mb-3">
              7A: Use of Google User Data
            </h3>
            <p>
              <strong>Data Access and Use</strong>
            </p>
            <p>
              If you choose to connect Hey Hoku with Google services (e.g.,
              Google login, Google Drive, Gmail, Calendar, or Photos), we may
              access limited Google user data solely to provide the requested
              features.
            </p>
            <p>Examples:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Using Google login to authenticate your account.</li>
              <li>
                Accessing Google Drive or Photos (if enabled by you) to import
                wardrobe images.
              </li>
              <li>
                Accessing Google Calendar (if enabled by you) to suggest outfits
                for scheduled events.
              </li>
            </ul>
            <p>
              We use this data only to deliver these features and for no other
              purpose.
            </p>

            <p>
              <strong>Data Sharing & Disclosure</strong>
            </p>
            <p>We do not sell or rent Google user data.</p>
            <p>
              Google user data is not shared with advertisers or unrelated third
              parties.
            </p>
            <p>Access is strictly limited to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Google Cloud Platform (for secure storage and hosting)</li>
              <li>
                Service providers necessary to operate Hey Hoku (e.g.,
                analytics, payment processors).
              </li>
            </ul>
            <p>
              All such providers act as processors under strict confidentiality
              and data protection agreements.
            </p>

            <p>
              <strong>Data Protection & Security</strong>
            </p>
            <p>
              Google user data is encrypted both in transit (TLS/SSL) and at
              rest.
            </p>
            <p>
              Access to Google user data is strictly role-based and limited to
              authorized personnel under confidentiality obligations.
            </p>
            <p>
              OAuth tokens and sensitive information are stored securely and
              never shared.
            </p>
            <p>
              We do not use Google user data for advertising or profiling beyond
              the direct features you enable.
            </p>

            <p>
              <strong>User Control & Deletion</strong>
            </p>
            <p>
              You may revoke Hey Hoku’s access to your Google account at any
              time from your Google Account settings.
            </p>
            <p>
              If you delete your Hey Hoku account, all Google user data and
              OAuth tokens associated with your account are permanently deleted
              from our systems within 30 days.
            </p>

            {/* === SECTION 8 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              8. INTERNATIONAL DATA TRANSFERS
            </h2>
            <p>Data is primarily processed in India.</p>
            <p>
              In future, as the service expands globally, data may be
              transferred to or stored in jurisdictions with different data
              protection standards. Where applicable, compliant mechanisms (such
              as contractual clauses) will be adopted.
            </p>

            {/* === SECTION 9 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">9. SECURITY</h2>
            <p>
              We implement reasonable technical and organizational safeguards,
              including encryption, access controls, secure cloud providers, and
              regular security audits.
            </p>
            <p>
              Nevertheless, no digital service can guarantee absolute security;
              users are responsible for using strong passwords and maintaining
              account security.
            </p>

            {/* === SECTION 10 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              10. CHILDREN’S PRIVACY
            </h2>
            <p>
              No age restrictions are enforced currently. Users under required
              age in their jurisdiction should acquire parental consent.
            </p>
            <p>
              If we learn that data has been submitted by a child in violation
              of law, the account/data will be deleted.
            </p>

            {/* === SECTION 11 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              11. MARKETING AND COMMUNICATIONS
            </h2>
            <p>
              You may opt out of marketing emails, push notifications, and SMS
              at any time via settings or unsubscribe links.
            </p>
            <p>
              Service-related messages (updates, security notices) will always
              be delivered.
            </p>

            {/* === SECTION 12 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              12. THIRD-PARTY LINKS
            </h2>
            <p>
              Our Services may include links to unrelated websites or apps. We
              do not control these and are not responsible for their privacy
              practices.
            </p>
            <p>
              We encourage you to review privacy notices of any third-party
              service you visit.
            </p>

            {/* === SECTION 13 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              13. UPDATES TO THIS POLICY
            </h2>
            <p>
              This Privacy Policy may be updated from time to time. Material
              changes will be notified to users directly via email or app
              notifications.
            </p>
            <p>Continued use indicates acceptance of updated terms.</p>

            {/* === SECTION 14 === */}
            <h2 className="font-bold text-2xl mt-8 mb-4">
              14. CONTACT INFORMATION
            </h2>
            <p>
              If you have questions or wish to make a privacy request, contact:
              <br />
              swethaa@heyhoku.ai / lilia@liliafashionhouse.com
            </p>
          </div>

          <nav className="mt-16 border-t border-[#eee] pt-8">
            <p className="font-winky text-[13px] text-[#767676] mb-3">Related policies</p>
            <div className="flex flex-wrap gap-4">
              <Link href="/legal/terms" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Terms of Service</Link>
              <Link href="/legal/cookies" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Cookie Policy</Link>
              <Link href="/support" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Contact Support</Link>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
